/**
 * PromEngineSummary - top-of-tab summary card for the Scrips PROM Engine
 * (cardiometabolic + longevity expansion 2026-05-07).
 *
 * Anchored to the prototype's "Scrips PROM Engine Summary" surface
 * (https://ai-native-prototype.vercel.app/care-pathway-studio.html PROMs
 * tab). The prototype copy reads "119 validated algorithms · all six
 * instruments included"; the production-path build replaces "all six
 * instruments" with the explicit cardiometabolic + longevity coverage
 * count to match Brand-Patch v2 voice (no TLP1-4 string anywhere) and
 * surface the engine's expansion beyond the original 6-instrument seed.
 *
 * The card is purely presentational: it reads the cardiometabolic +
 * longevity track counts off the canonical PROM_DEFINITIONS list at
 * render time so adding a new instrument refreshes the headline without
 * a copy edit.
 *
 * HC1: byte-stable - derived from the static PROM_DEFINITIONS list.
 * HC2: tokens-only - Tailwind classes resolve through Signal DS tokens.
 * HC3: cites the engine catalog as the source of the 119-instrument
 *   figure (the engine is the source of truth for the validated
 *   instrument count; the gap log tracks the canonical citation).
 * HC5: zero em / en dashes anywhere.
 * HC6 a11y: section uses `role="region"` + `aria-labelledby` so the
 *   screen reader announces "PROM Engine Summary" before the metric.
 */
import { useId, useMemo } from 'react';
import { PROM_DEFINITIONS } from './prom-definitions';

export interface PromEngineSummaryProps {
  /** Optional class hook for layout-specific overrides. */
  className?: string;
}

/**
 * Total validated instrument count cataloged in the Scrips PROM Engine.
 * Source: master prompt v3.3 §4.3 ("119 validated clinical scoring
 * instruments"). Tracked as PROM-CATALOG-CONFIRM in the gap log so the
 * canonical published figure can be cited inline once available.
 */
const ENGINE_VALIDATED_INSTRUMENT_COUNT = 119;

export function PromEngineSummary({ className }: PromEngineSummaryProps) {
  const titleId = useId();
  const counts = useMemo(() => {
    let cardiometabolic = 0;
    let longevity = 0;
    for (const def of PROM_DEFINITIONS) {
      if (def.track === 'cardiometabolic') cardiometabolic += 1;
      else if (def.track === 'longevity') longevity += 1;
    }
    return {
      cardiometabolic,
      longevity,
      total: cardiometabolic + longevity,
    };
  }, []);

  return (
    <section
      role="region"
      aria-labelledby={titleId}
      data-testid="prom-engine-summary"
      className={[
        'flex flex-col gap-gutter-sm rounded-signal-lg border border-surface-border bg-surface-base p-gutter-lg shadow-signal-card',
        className ?? '',
      ]
        .filter(Boolean)
        .join(' ')}
    >
      <header className="flex flex-col gap-1">
        <p className="text-signal-xs uppercase tracking-wide text-text-tertiary">
          Scrips PROM Engine
        </p>
        <h3
          id={titleId}
          className="text-signal-xl font-semibold text-text-primary"
        >
          {ENGINE_VALIDATED_INSTRUMENT_COUNT} validated instruments
          <span className="text-text-tertiary"> · </span>
          FHIR QuestionnaireResponse output
        </h3>
      </header>
      <p
        data-testid="prom-engine-summary-coverage"
        className="text-signal-sm text-text-secondary"
      >
        {counts.total} instruments seeded across cardiometabolic +
        longevity coverage
        <span className="text-text-tertiary"> · </span>
        {counts.cardiometabolic} cardiometabolic
        <span className="text-text-tertiary"> · </span>
        {counts.longevity} longevity
      </p>
      <p className="text-signal-xs text-text-tertiary">
        Engine catalog: master prompt v3.3 §4.3. Gap log:
        PROM-CATALOG-CONFIRM. Submission writes a QuestionnaireResponse
        plus a LOINC-coded Observation through the FHIR client; every
        submission emits an AuditEvent with source proms-tab.
      </p>
    </section>
  );
}
